/**
 * RecoverScreen v2 (PIN-dimenticato / disaster recovery)
 *
 * Door C. User has lost the PIN OR is on a brand new device with no
 * remote backup at all. They use the 24-word mnemonic.
 *
 * Flow:
 *   1. Input: @sigil + 24 words
 *   2. Client derives sign+box keys from mnemonic
 *   3. callable lookupMyAccount(sigilLower, pubKeySign)
 *      -> verifies the 24 words match the claimed sigil
 *   4. If matched -> ask for a NEW PIN
 *   5. Encrypt mnemonic with new PIN + sigil -> blob
 *   6. setBackup() to overwrite the old (possibly forgotten-PIN) backup
 *   7. Save locally and go to inbox
 *   8. If NOT matched -> error + offer to claim a new sigil with these words
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Frame, Header, Footer, Btn, Box, FieldLabel, Cursor,
} from '../components/Frame';
import { isValid, mnemonicToSeed } from '../lib/mnemonic';
import {
  initSodium, keypairFromSeed, deriveUid,
  boxKeypairFromSeed, toBase64Url,
} from '../lib/crypto';
import { useStore } from '../lib/store';
import { auth, callFn } from '../lib/firebase';
import { signInAsIdentity } from '../lib/authIdentity';
import { saveKeys } from '../lib/keys';
import { saveSession } from '../lib/storage';
import { publishMyKeysFromKeypairs } from '../lib/messaging';
import { ensureMyPrekeysPublished } from '../lib/fsMessaging';
import {
  isValidPin,
  isPinStrong,
  encryptMnemonic,
  saveLastSigil,
  resetLocalAttempts,
} from '../lib/pinCrypto';

const SIGIL_RE = /^[a-z0-9_]{3,20}$/i;

export default function RecoverScreen() {
  const navigate = useNavigate();
  const { setIdentity, setBoxKeys, setFirebaseUser, setSigil } = useStore();

  // Step 1: identify
  const [sigilInput, setSigilInput] = useState('');
  const [phrase, setPhrase] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // After successful 24-words verification
  const [verified, setVerified] = useState(null);
  // verified: { mnemonic, sigil, sigilLower, signKp, boxKp, cryptoUid, firebaseUser }

  // After verified: step 2 - new PIN
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [pinBusy, setPinBusy] = useState(false);
  const [pinErr, setPinErr] = useState(null);
  const [pinStage, setPinStage] = useState('idle');

  // After "not found": offer claim new sigil
  const [notFound, setNotFound] = useState(null);

  async function onVerify(e) {
    e?.preventDefault?.();
    setErr(null);
    setNotFound(null);

    const cleanSigil = sigilInput.trim().replace(/^@/, '').toLowerCase();
    const cleanPhrase = phrase.trim().toLowerCase().replace(/\s+/g, ' ');

    if (!SIGIL_RE.test(cleanSigil)) {
      setErr('sigil non valido');
      return;
    }
    if (!isValid(cleanPhrase)) {
      setErr('le 24 parole non sono BIP39 valide');
      return;
    }

    setBusy(true);
    try {
      // 1. Derive crypto material locally
      await initSodium();
      const seed = mnemonicToSeed(cleanPhrase);
      const signKp = await keypairFromSeed(seed);
      const boxKp = await boxKeypairFromSeed(seed);
      const cryptoUid = await deriveUid(signKp.publicKey);
      const pubKeySign = await toBase64Url(signKp.publicKey);

      // 2. Auth a token custom: abbiamo già le chiavi (mnemonica inserita), quindi
      //    autentichiamo direttamente come QUESTA identità (uid = deriveUid(pubKeySign)).
      await signInAsIdentity({ signPub: signKp.publicKey, signSec: signKp.secretKey });
      const cred = { user: auth().currentUser };

      // 3. Server-side verification: do these 24 words match this sigil?
      const fn = callFn('lookupMyAccount');
      const res = await fn({ pubKeySign, sigilLower: cleanSigil });
      const payload = res.data;

      if (!payload?.ok) {
        setErr(
          `server: ${payload?.error || 'sconosciuto'}` +
          (payload?.stage ? ` (${payload.stage})` : '')
        );
        return;
      }

      if (!payload.found) {
        // The 24 words don't match the sigil (or sigil doesn't exist)
        setNotFound({
          mnemonic: cleanPhrase,
          signKp,
          boxKp,
          cryptoUid,
          firebaseUser: cred.user,
        });
        return;
      }

      // SUCCESS: matches. Stash and move to new PIN setup.
      setVerified({
        mnemonic: cleanPhrase,
        sigil: payload.sigil,
        sigilLower: payload.sigilLower,
        signKp,
        boxKp,
        cryptoUid,
        firebaseUser: cred.user,
      });
    } catch (e) {
      console.error('recover verify failed', e);
      setErr(e?.message || 'errore sconosciuto');
    } finally {
      setBusy(false);
    }
  }

  async function onSetNewPin() {
    setPinErr(null);
    if (!isValidPin(newPin)) {
      setPinErr('PIN deve essere 6 cifre');
      return;
    }
    if (!isPinStrong(newPin)) {
      setPinErr('PIN troppo debole. Evita sequenze o ripetizioni.');
      return;
    }
    if (newPin !== confirmPin) {
      setPinErr('i due PIN non corrispondono');
      return;
    }
    if (!verified) return;

    setPinBusy(true);
    try {
      // 1. Encrypt mnemonic with new PIN
      setPinStage('deriving');
      const bundle = await encryptMnemonic({
        pin: newPin,
        sigilLower: verified.sigilLower,
        mnemonic: verified.mnemonic,
      });

      // 2. Overwrite server backup
      setPinStage('uploading');
      const fn = callFn('setBackup');
      const res = await fn({
        sigilLower: verified.sigilLower,
        blob: bundle.blob,
        salt: bundle.salt,
        nonce: bundle.nonce,
        argon2: bundle.argon2,
      });
      const payload = res.data;
      if (!payload?.ok) {
        throw new Error(
          `backup server: ${payload?.error || 'sconosciuto'}` +
          (payload?.stage ? ` (${payload.stage})` : '')
        );
      }

      // 3. Salva SOLO il sigil in localStorage (blob solo sul server, rate-limited)
      saveLastSigil(verified.sigil);

      // 4. Hydrate store
      setIdentity({
        publicKey: verified.signKp.publicKey,
        secretKey: verified.signKp.secretKey,
        uid: verified.cryptoUid,
        mnemonic: verified.mnemonic,
      });
      setBoxKeys?.({
        boxPub: verified.boxKp.publicKey,
        boxSec: verified.boxKp.secretKey,
      });
      setFirebaseUser(verified.firebaseUser);
      setSigil(verified.sigil);

      // Lega l'uid corrente al sigil recuperato (multi-device/migrazione: il
      // server aggiunge questo device a sigils/{sigil}.devices). Idempotente.
      toBase64Url(verified.signKp.publicKey)
        .then((pub) => callFn('registerSigil')({ sigil: verified.sigil, pubKeySign: pub }))
        .catch((e) => console.warn('registerSigil (recover)', e));

      // Ripubblica le chiavi pubbliche (idempotente): un utente recuperato
      // deve restare raggiungibile.
      try {
        await publishMyKeysFromKeypairs(
          verified.signKp.publicKey,
          verified.boxKp.publicKey,
        );
      } catch (e) { console.warn('publishKeys (recover)', e); }
      // Forward Secrecy: pubblica le prekey su questo device.
      ensureMyPrekeysPublished({
        signKeypair: { pub: verified.signKp.publicKey, priv: verified.signKp.secretKey },
        sigil: verified.sigil,
      }).catch((e) => console.warn('prekeys (recover)', e));

      try {
        await saveKeys({
          mnemonic: verified.mnemonic,
          uid: verified.cryptoUid,
          sigil: verified.sigil,
          signPub: verified.signKp.publicKey,
          signSec: verified.signKp.secretKey,
          boxPub: verified.boxKp.publicKey,
          boxSec: verified.boxKp.secretKey,
          securityLevel: 1,
        });
      } catch (e) { console.warn('saveKeys', e); }

      try {
        saveSession({
          mnemonic: verified.mnemonic,
          uid: verified.cryptoUid,
          sigil: verified.sigil,
        });
      } catch (e) { console.warn('saveSession', e); }

      resetLocalAttempts();
      navigate('/inbox', { replace: true });
    } catch (e) {
      console.error('new PIN set failed', e);
      setPinErr(e?.message || 'errore sconosciuto');
      setPinStage('idle');
    } finally {
      setPinBusy(false);
    }
  }

  function goClaimNewSigil() {
    if (!notFound) return;
    // Hydrate store and go to /sigil to claim a new sigil with these words
    setIdentity({
      publicKey: notFound.signKp.publicKey,
      secretKey: notFound.signKp.secretKey,
      uid: notFound.cryptoUid,
      mnemonic: notFound.mnemonic,
    });
    setBoxKeys?.({
      boxPub: notFound.boxKp.publicKey,
      boxSec: notFound.boxKp.secretKey,
    });
    setFirebaseUser(notFound.firebaseUser);
    navigate('/sigil');
  }

  // ----- RENDER -----

  if (verified) {
    return (
      <Frame statusLeft="RECOVER :: NEW_PIN" statusRight="5G">
        <Header title="// IMPOSTA NUOVO PIN" />

        <Box variant="success" className="text-[16px] mb-3">
          ✓ identità verificata. sigil: @{verified.sigil}
        </Box>

        <p className="text-[16px] text-ink-dim mb-3">
          scegli un nuovo PIN a 6 cifre. sostituirà il vecchio.
        </p>

        <div className="space-y-3">
          <div>
            <FieldLabel>&gt; NUOVO PIN</FieldLabel>
            <input
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={newPin}
              onChange={(e) => setNewPin(e.target.value.replace(/[^0-9]/g, ''))}
              disabled={pinBusy}
              className="w-full bg-black border border-ink-dim text-ink text-glow font-mono text-[20px] tracking-[0.3em] px-3 py-2 focus:border-ink focus:outline-none text-center"
              placeholder="••••••"
            />
          </div>
          <div>
            <FieldLabel>&gt; RIPETI PIN</FieldLabel>
            <input
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value.replace(/[^0-9]/g, ''))}
              disabled={pinBusy}
              className="w-full bg-black border border-ink-dim text-ink text-glow font-mono text-[20px] tracking-[0.3em] px-3 py-2 focus:border-ink focus:outline-none text-center"
              placeholder="••••••"
            />
          </div>

          {pinErr && (
            <Box variant="danger" className="text-[16px]">
              <span className="text-red-500">✕</span> {pinErr}
            </Box>
          )}
          {pinBusy && (
            <Box variant="warn" className="text-[16px]">
              {pinStage === 'deriving' && (<>derivando chiave<Cursor /></>)}
              {pinStage === 'uploading' && (<>aggiornando backup<Cursor /></>)}
            </Box>
          )}
        </div>

        <Footer>
          <Btn
            variant="primary"
            onClick={onSetNewPin}
            disabled={pinBusy || newPin.length !== 6 || confirmPin.length !== 6}
          >
            {pinBusy ? '...' : 'Salva il nuovo PIN ▶'}
          </Btn>
        </Footer>
      </Frame>
    );
  }

  if (notFound) {
    return (
      <Frame statusLeft="RECOVER :: NOT_FOUND" statusRight="5G">
        <Header title="// NON RICONOSCIUTO" />

        <Box variant="warn" className="text-[16px] mb-3 space-y-2">
          <p className="text-amber">
            ✕ le 24 parole sono BIP39 valide, ma NON risultano associate al
            sigil @{sigilInput.trim().replace(/^@/, '')}.
          </p>
          <p className="text-ink-dim">possibili motivi:</p>
          <ul className="text-ink-dim text-[13px] list-disc pl-4 space-y-1">
            <li>hai sbagliato anche solo una parola o l'ordine</li>
            <li>hai digitato un sigil sbagliato</li>
            <li>queste 24 parole non sono mai state usate su Nullum</li>
          </ul>
        </Box>

        <div className="flex gap-2 mt-4">
          <Btn variant="ghost" onClick={() => setNotFound(null)}>
            ◀ riprova
          </Btn>
          <Btn onClick={goClaimNewSigil}>
            ▶ usa queste parole per un nuovo sigil
          </Btn>
        </div>
      </Frame>
    );
  }

  return (
    <Frame statusLeft="RECOVER :: 24_WORDS" statusRight="5G">
      <Header
        title="// HO PERSO IL PIN"
        crumb="◀ back"
        onCrumbClick={() => navigate('/welcome')}
      />

      <p className="text-[16px] text-ink-dim mb-3">
        per rientrare senza PIN servono il tuo sigil e le 24 parole della
        mnemonica salvate al primo onboarding.
      </p>

      <form onSubmit={onVerify} className="space-y-3">
        <div>
          <FieldLabel>&gt; SIGIL</FieldLabel>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-dim pointer-events-none select-none text-[16px]">@</span>
            <input
              type="text"
              name="sigil"
              autoComplete="off"
              spellCheck="false"
              value={sigilInput}
              onChange={(e) => setSigilInput(e.target.value)}
              placeholder="iltuonome"
              disabled={busy}
              className="w-full bg-black border border-ink-dim text-ink font-mono text-[16px] pl-7 pr-3 py-2 focus:border-ink focus:outline-none"
            />
          </div>
        </div>

        <div>
          <FieldLabel>&gt; 24 PAROLE</FieldLabel>
          <textarea
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            rows={5}
            disabled={busy}
            className="w-full bg-black border border-ink-dim text-ink text-glow font-mono text-[15px] py-2 px-2 outline-none focus:border-ink resize-none"
            placeholder="word1 word2 word3 ..."
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
          />
          <p className="text-[13px] text-ink-dim mt-1">
            BIP39 standard · separate da spazio
          </p>
        </div>

        <Box variant="warn" className="text-[13px] text-ink-dim">
          <span className="text-amber">!</span> le 24 parole non vengono inviate al server.
          inviamo solo la chiave pubblica derivata.
        </Box>

        {err && (
          <Box variant="danger" className="text-[16px]">
            <span className="text-red-500">✕</span> {err}
          </Box>
        )}

        <Btn
          type="submit"
          variant="primary"
          disabled={
            busy ||
            !sigilInput.trim() ||
            phrase.trim().split(/\s+/).length < 24
          }
        >
          {busy
            ? (<>verifico<Cursor /></>)
            : 'Verifica e imposta un nuovo PIN ▶'}
        </Btn>
      </form>
    </Frame>
  );
}
